import React, { useCallback, useContext } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { AskingPrice, AskingPriceFromTo } from 'models/Listing';
import { getHrefFromUrl, isUrl, LESS_50_K, mapToMoneyFormat } from 'utils';
import { formatPhoneNumber } from 'phoneNumberUtils';
import { ExpandableText } from 'components/ExpandableText';
import ListingContext from '../../../../providers/Listing/Listing.context';

import './ListingInformationSection.styles.scss';
import './ListingInformationSection.styles.responsive.scss';

type ListingContentProps = {
  content: Record<string, any>;
};

const ListingContent: React.FC<ListingContentProps> = ({ content }) => {
  const { t } = useTranslation();

  const { listing } = useContext(ListingContext);

  const formatData = useCallback(
    (
      key: string,
      value: string | number | AskingPrice | AskingPriceFromTo,
    ): JSX.Element => {
      if (!value) return <p>-</p>;

      if (key === 'phone') return <p>{formatPhoneNumber(value as string)}</p>;

      const askingPriceValue = value as AskingPriceFromTo;

      if (askingPriceValue?.amountFrom && askingPriceValue?.amountTo) {
        return (
          <p>{`${mapToMoneyFormat(
            key,
            askingPriceValue.amountFrom,
          )}-${mapToMoneyFormat(key, askingPriceValue.amountTo)}`}</p>
        );
      }

      const val =
        key === 'askingPricePriceRange' ? (value as AskingPrice).amount : value;

      const stringVal = val ? (mapToMoneyFormat(key, val) as string) : '-';

      const stringValue =
        stringVal === LESS_50_K
          ? `${stringVal} (${t('approximately')})`
          : stringVal;

      return isUrl(stringValue) ? (
        <a href={getHrefFromUrl(stringValue)}>{t('viewLink')}</a>
      ) : (
        <ExpandableText content={stringVal} />
      );
    },
    [t],
  );

  return (
    <ul>
      {Object.entries(content).map(([key, value]) => {
        if (key === 'location' && !value) return null;

        if (key === 'locationPostcode') {
          if (!value) return null;

          return (
            <li
              key={key}
              className={classNames('listing-information-section__content')}
            >
              <p>{t('locationPostcode')}</p>
              <span>{value}</span>
            </li>
          );
        }

        if (key === 'ebitda' && value < 0) return null;

        if (
          key === 'ownersAlsoOwnCommercialProperty' &&
          !listing?.hasCommercialProperty
        ) {
          return null;
        }

        return (
          <li
            key={key}
            className={classNames('listing-information-section__content')}
          >
            <p>{t(key)}</p>
            <div>
              {Array.isArray(value)
                ? value.length
                  ? value.map((val, index) => (
                      <div key={key + index}>{formatData(key, val)}</div>
                    ))
                  : '-'
                : formatData(key, value)}
            </div>
          </li>
        );
      })}
    </ul>
  );
};

type ListingInformationSectionProps = {
  className?: string;
  headerText?: string;
  content: Record<string, string | string[] | number[]>;
};

const ListingInformationSection: React.FC<ListingInformationSectionProps> = ({
  content,
}) => {
  return (
    <section className="listing-information-section">
      <ListingContent content={content} />
    </section>
  );
};
export default ListingInformationSection;
